import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { useVigoLogout } from 'src/@core/hooks/useVigoLogout';
import debug from 'debug';

const debugLog = debug('auth:IdleTimer');

const whitelist = ['/dashboards/[name]', '/dashboards/development', '/signedout', '/tracking'];

export const IdleTimer = () => {
  const router = useRouter();
  const minutesBeforeSignout = 20;

  const { handleLogout } = useVigoLogout();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const goBackToLogin = useCallback(() => {
    handleLogout();
  }, [handleLogout]);

  const restartAutoReset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      goBackToLogin();
    }, minutesBeforeSignout * 1000 * 60);
  }, [goBackToLogin, minutesBeforeSignout]);

  const preventReset = useMemo(() => {
    if (window.location.origin === 'http://localhost:3000') {
      debugLog('Preventing reset for localhost', window.location.origin);

      return true;
    }

    return whitelist.includes(router.pathname);
  }, [router.pathname]);

  useEffect(() => {
    if (preventReset) return;

    restartAutoReset();

    const opts = { passive: true } as const;

    window.addEventListener('mousemove',   restartAutoReset);
    window.addEventListener('keydown',     restartAutoReset);
    window.addEventListener('wheel',       restartAutoReset, opts);
    window.addEventListener('click',       restartAutoReset);
    window.addEventListener('pointerdown', restartAutoReset);
    window.addEventListener('touchstart',  restartAutoReset, opts);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('mousemove',   restartAutoReset);
      window.removeEventListener('keydown',     restartAutoReset);
      window.removeEventListener('wheel',       restartAutoReset);
      window.removeEventListener('click',       restartAutoReset);
      window.removeEventListener('pointerdown', restartAutoReset);
      window.removeEventListener('touchstart',  restartAutoReset);
    };
  }, [preventReset, restartAutoReset, router.pathname]);

  return null;
};
